
import React, { useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'

interface FilteredMultiSelectDropdownProps {
  options: FilteredMultiSelectDropdownOption[];
  disabled?: boolean;
  value: string[] | number[];
  label?: string;
  onChange: (newValue: string[] | number[]) => void;
}

interface FilteredMultiSelectDropdownOption {
  value: string | number;
  displayText: string;
  subText?: string;
}

export default function FilteredMultiSelectDropdown({label, disabled, onChange, options, value}: FilteredMultiSelectDropdownProps) {
  const [query, setQuery] = useState('')

  const filteredOptions =
    query === ''
      ? options
      : options?.filter((opt) => {
          return `${opt.displayText}${opt?.subText ?? ''}`.toLowerCase().includes(query.toLowerCase())
        })

  return (
    <Combobox as="div" value={value} onChange={onChange} disabled={disabled} multiple>
      <Label className="block text-sm font-medium leading-6 text-gray-900">{label ?? ''}</Label>
      <div className="relative mt-2">
        <ComboboxInput
          aria-disabled={disabled}
          className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
          onChange={(event) => setQuery(event.target.value)}
          displayValue={(val: string[] | number[]) => {
            const allDisplayNames = val.map((v: string | number) => {
              const findByValue = filteredOptions?.find(op => op.value === v);
              return findByValue?.displayText;
            })

            return allDisplayNames.join(',');
          }}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        {filteredOptions?.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option.value}
                className={({ active }) => `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`}
              >
                {({ active, selected }) => (
                  <>
                    <div className="flex">
                      <span className={`truncate ${selected ? 'font-semibold' : ''}`}>{option.displayText}</span>
                      <span
                        className={`ml-2 truncate capitalize text-gray-500 ${active ? 'text-blue-200' : 'text-gray-500'}`}
                      >
                        {option?.subText ?? ''}
                      </span>
                    </div>

                    {selected && (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-blue-600'}`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}
